import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from '../../../../state/auth';
import { AppState } from '../../../../state/configureStore';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { ISort, PageFilter } from '../../../../state/app';
import { DateTimeDisplay } from '../../../components/DateTimeDisplay';
import TaskAndSubtaskLinkButton from '../../../components/TaskAndSubtaskLinkButton';
import {
  fetchTaskActivityListRelatedToUserId,
  clearCommentDashboard,
} from './commentDashboard.actions';
import { CommentDashboardState } from './commentDashboard.reducer';
import { DisplayHtmlContent } from '../../../components';
import { groupBy } from 'lodash';
import { TablePageSize } from '../../../../constants';
import { Paper, TablePagination, Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const Dashboard = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { userId } = useSelector<AppState, AuthState>((state) => state.auth);
  const { items: taskActivityList, totalItems } = useSelector<
    AppState,
    CommentDashboardState
  >((state) => state.commentDashboard);
  const [paging, setPaging] = useState<PageFilter>({
    pageNumber: 0,
    pageSize: 10,
  });
  const sort: ISort = useMemo(() => ({ orderBy: 'createdAt', direction: 'desc' }), []);

  const handleChangePageNumber = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPaging({ ...paging, pageNumber: newPage });
  };

  const handleChangePageSize = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPaging({
      ...paging,
      pageSize: +event.target.value,
      pageNumber: 0,
    });
  };

  useEffect(() => {
    if (userId) {
      dispatch(fetchTaskActivityListRelatedToUserId(userId, { ...paging, sort }));
    }
    return function cleanup() {
      dispatch(clearCommentDashboard());
    };
  }, [dispatch, userId, paging, sort]);

  // narrow types: keep only items that have smTask and taskNumber
  const validActivities = Array.isArray(taskActivityList)
    ? taskActivityList.filter((ta): ta is any =>
        Boolean(ta && ta.smTask && ta.smTask.taskNumber),
      )
    : [];

  const groups = groupBy(
    validActivities,
    (taskActivity) => taskActivity.smTask.taskNumber,
  );

  const styles = {
    root: {
      background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
      border: '1px solid rgba(226, 232, 240, 0.9)',
      borderRadius: 4,
      overflow: 'hidden',
    },
    header: {
      borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
      padding: theme.spacing(2.5, 3, 2),
    },
    title: {
      color: '#0f172a',
      fontSize: '1.25rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    subtitle: {
      color: '#64748b',
      fontSize: '0.95rem',
      marginTop: theme.spacing(0.5),
    },
    titleRow: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
    },
    titleIcon: {
      color: '#2563eb',
      fontSize: 22,
    },
    content: {
      padding: theme.spacing(2, 2, 1.5),
    },
    commentList: {
      display: 'grid',
      gap: theme.spacing(1.5),
    },
    comment: {
      background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
      border: '1px solid rgba(191, 219, 254, 0.7)',
      borderLeft: '4px solid #2563eb',
      borderRadius: 4,
      boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
      overflow: 'hidden',
    },
    commentMeta: {
      backgroundColor: 'rgba(239, 246, 255, 0.75)',
      borderBottom: '1px solid rgba(191, 219, 254, 0.5)',
      color: '#334155',
      display: 'flex',
      flexDirection: 'column',
      flexWrap: 'wrap',
      gap: theme.spacing(0.75),
      padding: theme.spacing(1.25, 1.5),
    },
    metaTitleRow: {
      alignItems: 'center',
      display: 'flex',
      width: '100%',
    },
    metaDetailRow: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1.25),
      width: '100%',
    },
    commentMetaText: {
      color: '#334155',
      fontWeight: 600,
    },
    commentTitleText: {
      color: '#1e293b',
      fontSize: '0.95rem',
      fontWeight: 700,
    },
    metaItem: {
      alignItems: 'center',
      display: 'inline-flex',
      gap: theme.spacing(0.5),
    },
    metaIcon: {
      color: '#2563eb',
      fontSize: 16,
    },
    timestamp: {
      color: '#64748b',
    },
    commentBody: {
      color: '#1e293b',
      padding: theme.spacing(1.5),
      '& p:first-child': {
        marginTop: 0,
      },
      '& p:last-child': {
        marginBottom: 0,
      },
    },
    emptyState: {
      color: '#64748b',
      padding: theme.spacing(3, 1),
      textAlign: 'center',
    },
    pagination: {
      borderTop: '1px solid rgba(226, 232, 240, 0.8)',
      marginTop: theme.spacing(2),
      paddingTop: theme.spacing(1),
    },
  } as const;

  return (
    <Paper sx={styles.root} elevation={2}>
      <Box sx={styles.header}>
        <Box sx={styles.titleRow}>
          <ChatBubbleOutlineIcon sx={styles.titleIcon} />
          <Typography variant="h6" sx={styles.title}>
            Last comments
          </Typography>
        </Box>
        <Typography variant="body2" sx={styles.subtitle}>
          Track the latest discussion updates across your assigned tasks.
        </Typography>
      </Box>
      <Box sx={styles.content}>
        {Object.keys(groups).length === 0 ? (
          <Typography variant="body2" sx={styles.emptyState}>
            No comments found for this page.
          </Typography>
        ) : (
          <Box sx={styles.commentList}>
            {Object.keys(groups).map((taskNumber) => {
              const taskActivities = groups[taskNumber];
              const startActivity = [...taskActivities]
                .filter((taskActivity) => taskActivity.createdAt)
                .sort(
                  (x, y) =>
                    +new Date(x.createdAt as string) - +new Date(y.createdAt as string),
                )
                .pop();

              if (!startActivity) return null;

              return (
                <Paper sx={styles.comment} key={startActivity.id} elevation={0}>
                  <Box sx={styles.commentMeta}>
                    <Box sx={styles.metaTitleRow}>
                      <Box component="span" sx={styles.metaItem}>
                        <ChatBubbleOutlineIcon sx={styles.metaIcon} />
                        <Typography
                          component="span"
                          variant="caption"
                          sx={styles.commentTitleText}
                        >
                          <TaskAndSubtaskLinkButton taskActivity={startActivity} />
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={styles.metaDetailRow}>
                      <Box component="span" sx={styles.metaItem}>
                        <PersonOutlineIcon sx={styles.metaIcon} />
                        <Typography
                          component="span"
                          variant="caption"
                          sx={styles.commentMetaText}
                        >
                          {startActivity.user?.fullName || 'Unknown user'}
                        </Typography>
                      </Box>
                      <Box component="span" sx={styles.metaItem}>
                        <AccessTimeIcon sx={styles.metaIcon} />
                        <Typography
                          component="span"
                          variant="caption"
                          sx={styles.timestamp}
                        >
                          <DateTimeDisplay value={startActivity.createdAt} />
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  {/* Comment body section */}
                  <Box sx={styles.commentBody}>
                    <DisplayHtmlContent>{startActivity.comment}</DisplayHtmlContent>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
        <Box sx={styles.pagination}>
          <TablePagination
            rowsPerPageOptions={[...TablePageSize]}
            component="div"
            count={totalItems}
            rowsPerPage={paging.pageSize}
            page={paging.pageNumber}
            onPageChange={handleChangePageNumber}
            onRowsPerPageChange={handleChangePageSize}
            labelRowsPerPage="Comments per page"
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default Dashboard;
